/**
 * Unified toast notification hook
 *
 * Features:
 * - Single toast at a time with internal queue
 * - 4-second auto-dismiss
 * - Optional manual dismiss
 * - Success/Error/Info/Warning variants
 */

import { useState, useCallback, useRef, useEffect } from "react";

export type ToastVariant = "success" | "error" | "info" | "warning";

interface ToastMessage {
  id: number;
  message: string;
  variant: ToastVariant;
}

interface ToastState {
  current: ToastMessage | null;
  queue: ToastMessage[];
}

let toastIdCounter = 0;

// Global state for toast management
let globalSetState: ((state: ToastState) => void) | null = null;
let globalState: ToastState = { current: null, queue: [] };

/**
 * Process next toast from queue
 */
function processQueue() {
  if (globalState.current || globalState.queue.length === 0) {
    return;
  }

  const next = globalState.queue.shift();
  if (next && globalSetState) {
    globalSetState({ ...globalState, current: next });

    // Auto-dismiss after 4 seconds
    setTimeout(() => {
      dismissToast();
    }, 4000);
  }
}

/**
 * Show a toast notification
 */
export function showToast(message: string, variant: ToastVariant = "info") {
  const toast: ToastMessage = {
    id: ++toastIdCounter,
    message,
    variant,
  };

  if (!globalState.current) {
    // No toast showing, show immediately
    if (globalSetState) {
      globalSetState({ ...globalState, current: toast });

      // Auto-dismiss after 4 seconds
      setTimeout(() => {
        dismissToast();
      }, 4000);
    }
  } else {
    // Queue it
    globalState.queue.push(toast);
    if (globalSetState) {
      globalSetState({ ...globalState });
    }
  }
}

/**
 * Dismiss current toast and show next in queue
 */
export function dismissToast() {
  if (globalSetState) {
    globalSetState({ ...globalState, current: null });

    // Process next in queue after dismiss animation
    setTimeout(() => {
      processQueue();
    }, 300);
  }
}

/**
 * Toast hook - call once in app root or layout
 */
export function useToast() {
  const [state, setState] = useState<ToastState>(globalState);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    globalSetState = setState;
    globalState = state;

    return () => {
      mounted.current = false;
      if (globalSetState === setState) {
        globalSetState = null;
      }
    };
  }, [state]);

  return {
    current: state.current,
    dismiss: useCallback(() => dismissToast(), []),
  };
}

/**
 * Convenience methods for specific variants
 */
export const toast = {
  success: (message: string) => showToast(message, "success"),
  error: (message: string) => showToast(message, "error"),
  info: (message: string) => showToast(message, "info"),
  warning: (message: string) => showToast(message, "warning"),
};
